/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Tooltip } from '@mui/material';
import {
  MapPin, Clock, Camera,
} from 'lucide-react';
import { UserContext } from '../../contexts/User';
import DeleteTeamModal from './DeleteTeamModal';
import EditTeamModal from './EditTeamModal';
import TeamEditColumn from './TeamEditColumn';
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

function engagementTone(value) {
  if (value >= 70) return 'text-shoot-success';
  if (value >= 40) return 'text-[#F19235]';
  return 'text-[#b94f3c]';
}

function MonitorDot({ active, icon: Icon, label }) {
  return (
    <Tooltip title={`${label} monitoring ${active ? 'on' : 'off'}`} arrow>
      <span
        className={`inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition ${
          active
            ? 'bg-shoot-success/15 text-shoot-success'
            : 'bg-[#fbeee9] text-[#b94f3c]'
        }`}
        aria-label={`${label} monitoring ${active ? 'on' : 'off'}`}
      >
        <Icon size={16} />
      </span>
    </Tooltip>
  );
}

function TeamCard({
  name,
  percentage,
  managers,
  members,
  teamId,
  locationViolations,
  webcamViolations,
  onTeamDeleted,
  workspaceId,
  onTeamEdited,
  dateRange,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { redirect, member } = useContext(UserContext);
  const isWorkspaceAdmin = getMemberRoleLevel(member) >= accountTypeLevel['Workspace admin'];

  const tone = engagementTone(percentage);
  const monitoringTimeActive = percentage >= 21;
  const managerLabel = managers && managers.length > 0 ? managers[0].name : '—';

  const openTeam = () => {
    const [startDate, endDate] = dateRange || [];
    const start = startDate ? moment(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss') : null;
    const end = endDate ? moment(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss') : null;
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    redirect(`/members/team-engagement/${teamId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openTeam}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openTeam();
        }
      }}
      className={`group relative cursor-pointer rounded-xl 
      bg-white border border-surface-2 ${softShadow} 
      p-4 transition hover:shadow-lg hover:-translate-y-0.5 
      focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40`}
    >
      {showDeleteModal && (
        <DeleteTeamModal
          closeModal={() => setShowDeleteModal(false)}
          onTeamDeleted={onTeamDeleted}
          name={name}
          members={members}
          managers={managers}
          teamId={teamId}
          workspaceId={workspaceId}
        />
      )}
      {showEditModal && (
        <EditTeamModal
          closeModal={() => setShowEditModal(false)}
          onTeamEdited={onTeamEdited}
          name={name}
          members={members}
          managers={managers}
          teamId={teamId}
        />
      )}

      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-base font-semibold text-ink-900 truncate flex-1 min-w-0">
          {name}
        </h3>
        <div
          className="flex items-center gap-1 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <MonitorDot active={!locationViolations} icon={MapPin} label="Location" />
          <MonitorDot active={monitoringTimeActive} icon={Clock} label="Time" />
          <MonitorDot active={!webcamViolations} icon={Camera} label="Webcam" />
          {isWorkspaceAdmin && (
            <TeamEditColumn
              deleteTeam={() => setShowDeleteModal(true)}
              editTeam={() => setShowEditModal(true)}
            />
          )}
        </div>
      </div>

      <p className={`text-3xl font-bold leading-none mb-3 ${tone}`}>
        {percentage}
        %
      </p>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-ink-500 uppercase tracking-wider text-[10px] font-semibold w-16 flex-shrink-0">
            Manager
          </span>
          <span className="text-ink-900 font-medium truncate">{managerLabel}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-ink-500 uppercase tracking-wider text-[10px] font-semibold w-16 flex-shrink-0">
            Members
          </span>
          <span className="text-ink-900 font-medium">{members.length}</span>
        </div>
      </div>
    </div>
  );
}

TeamCard.propTypes = {
  name: PropTypes.string.isRequired,
  percentage: PropTypes.number.isRequired,
  managers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  teamId: PropTypes.number.isRequired,
  workspaceId: PropTypes.number.isRequired,
  locationViolations: PropTypes.bool,
  webcamViolations: PropTypes.bool,
  onTeamDeleted: PropTypes.func.isRequired,
  onTeamEdited: PropTypes.func.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
};

export default TeamCard;
